import _ from 'lodash';
import { LibraryListItem, VideoListItem } from './libraryListItem';
import { Typography } from '@design-system/components/typography/typography';
import { CircularProgress, Skeleton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { Folder } from '../../models/videos.domain';
import { useElementObserver } from '@client/new/hooks/useElementObserver';
import { useEffect, useState } from 'react';
import { ContentFilters } from '@client/new/common/library/helpers';

export type FolderListItem = {
  id: number;
  name: string;
  count: string;
  parentId: number | null;
  updatedAt: string;
  onClick: () => void;
};

export type LibraryListProps = {
  selectVideo: (id: string) => void;
  folders: FolderListItem[];
  videos: VideoListItem[];
  selectedIds: string[] | null;
  loading: boolean;
  searchText: string;
  fetchError: boolean;
  smallerView: boolean;
  currentFolder: (Folder & { count: string }) | null;
  activeVideoFilter?: ContentFilters | null;
};
export const LibraryList = ({
  videos,
  folders,
  selectedIds,
  loading,
  selectVideo,
  searchText,
  fetchError,
  smallerView,
  currentFolder,
  activeVideoFilter,
}: LibraryListProps) => {
  if (fetchError) {
    return (
      <div
        className={clsx(
          'flex flex-col justify-center items-center',
          smallerView && 'h-[180px]',
          !smallerView && 'h-[400px]',
        )}
      >
        Something went wrong. Please reload the page.
      </div>
    );
  }
  if (loading) {
    return (
      <div
        className={clsx(
          'w-full flex flex-col items-center gap-2',
          smallerView && 'h-[180px]',
          !smallerView && 'h-[400px]',
        )}
      >
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton variant="rounded" height={50} key={i} className="w-full" />
        ))}
      </div>
    );
  }

  const noVideos = _.isEmpty(videos);
  const noFolders = _.isEmpty(folders);

  if (!searchText && noVideos && noFolders && !currentFolder) {
    return (
      <div
        className={clsx(
          'flex flex-col justify-center items-center',
          smallerView && 'h-[180px]',
          !smallerView && 'h-[200px]',
        )}
      >
        <Typography variant="bodyLarge">
          Looks like you have no videos in your Library!
        </Typography>
        <Typography variant="bodyLarge">Upload your files first.</Typography>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'overflow-auto flex flex-col gap-2',
        smallerView && 'h-[180px]',
        !smallerView && 'h-[360px]',
      )}
    >
      <Typography>Folders</Typography>

      {noFolders && searchText ? (
        <Result text="No result" />
      ) : noFolders ? (
        <Result
          text={!activeVideoFilter ? 'No content' : 'No matching content'}
        />
      ) : (
        folders.map((folder) => (
          <div
            key={folder.id}
            className="flex gap-4 cursor-pointer"
            onClick={folder.onClick}
          >
            <FontAwesomeIcon icon={faFolder} />
            <div className="flex flex-col gap-1">
              <Typography variant="body" size="medium">
                {folder.name}
              </Typography>
              <Typography variant="body" size="small" color="N-80">
                {folder.count}
              </Typography>
            </div>
          </div>
        ))
      )}
      <Typography>Files</Typography>
      {noVideos && searchText ? (
        <Result text="No result" />
      ) : noVideos ? (
        <Result
          text={!activeVideoFilter ? 'No content' : 'No matching content'}
        />
      ) : (
        <VirtualizedVideoList
          selectedIds={selectedIds}
          selectVideo={selectVideo}
          videos={videos}
        />
      )}
    </div>
  );
};

const Result: React.FC<{ text: string }> = ({ text }) => (
  <div className={clsx('flex flex-col justify-center items-center')}>
    <Typography variant="body" size="large">
      {text}
    </Typography>
  </div>
);

const VirtualizedVideoList = ({
  videos,
  selectedIds,
  selectVideo,
}: Pick<LibraryListProps, 'videos' | 'selectedIds' | 'selectVideo'>) => {
  const observedId = 'addFronLibraryVideoList';
  const { isVisibleInViewport } = useElementObserver({ observedId });
  const [list, setList] = useState<VideoListItem[]>([]);

  useEffect(() => {
    setList(videos.slice(0, list.length + 20));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisibleInViewport]);

  useEffect(() => {
    setList(videos.slice(0, 20));
  }, [videos]);

  const hideLoader = videos.length === list.length;

  return (
    <>
      {list.map((i) => (
        <LibraryListItem
          key={i.id}
          {...i}
          selected={!!selectedIds && selectedIds.includes(i.id)}
          selectedIndx={
            selectedIds && selectedIds.length === 1
              ? undefined
              : selectedIds?.indexOf(i.id)
          }
          selectVideo={selectVideo}
        />
      ))}
      <CircularProgress
        id={observedId}
        className={clsx(hideLoader && 'hidden', !hideLoader && 'visible')}
      />
    </>
  );
};
